import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Chip,
  Alert,
  Divider,
  Pagination,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type {
  HubSpotSearchResponse,
  SearchResult,
  ValidationSummary as HubSpotValidationSummary,
  HubSpotFieldForMapping
} from '../../../types/hubspot';
import {
  hasLookupSuggestionChoices,
  type SalesforceFieldForMapping,
  type SalesforceInputDuplicateGroup,
  type SalesforcePreviewConflictResponse,
  type ValidationSummary as SalesforceValidationSummaryWire,
  type UserConfirmedLookups,
  type UpsertMappedPreviewResponse
} from '../../../types/salesforce';
import CrmValidationSummarySection from './CrmValidationSummarySection';
import {
  HUBSPOT_VALIDATION_SUMMARY_DESCRIPTION,
  HUBSPOT_VALIDATION_SUMMARY_TITLE,
  SALESFORCE_VALIDATION_SUMMARY_DESCRIPTION,
  SALESFORCE_VALIDATION_SUMMARY_TITLE
} from './crmValidationSummaryCopy';
import SalesforceLookupSuggestionsSection from './SalesforceLookupSuggestionsSection';
import { formatCanonicalCompositeKeyForDisplay } from '../../../utils/crmCompositePrimaryKey';

/** Radio value meaning "ignore row" – keep existing CRM record, do not update. */
export const IGNORE_VALUE = '__ignore__';

const DUPLICATE_ROW_HINT_MAX = 3;
const DUPLICATE_CELL_DISPLAY_MAXLEN = 96;

/** Spreadsheet column hints for distinguishing duplicate spreadsheet rows (excludes PK / search cols in parent). */
export interface DuplicateRowHintField {
  sourceCol: string;
  /** Human-readable spreadsheet column heading */
  label: string;
}

function duplicateRowHintsForDisplay(
  row: Record<string, unknown> | undefined,
  hintFields: DuplicateRowHintField[],
  maxHints: number,
  maxValueLen: number
): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  if (!row || hintFields.length === 0 || maxHints <= 0) return out;
  for (const { sourceCol, label } of hintFields) {
    if (out.length >= maxHints) break;
    const raw = row[sourceCol];
    if (raw == null) continue;
    const text = String(raw).trim();
    if (text === '') continue;
    const value =
      text.length > maxValueLen
        ? `${text.slice(0, Math.max(0, maxValueLen - 1))}…`
        : text;
    out.push({ label, value });
  }
  return out;
}
const CONFLICT_LIST_PAGE_SIZE = 50;

export interface SyncConflictModalProps {
  open: boolean;
  /** HubSpot search job vs Salesforce preview duplicate matches (same modal wire shape). */
  searchResults:
    | HubSpotSearchResponse
    | SalesforcePreviewConflictResponse
    | null;
  /** Current selections: normalized_key -> CRM record ID to update, or null/IGNORE_VALUE to ignore (keep existing) */
  userSelections: Record<string, string | null>;
  onSelectionChange: (
    normalizedKey: string,
    crmRecordId: string | null
  ) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional map from normalized_key -> display label (e.g. original value) */
  keyDisplayMap?: Map<string, string>;
  confirmLabel?: string;
  disabled?: boolean;
  validationSummary?:
    | HubSpotValidationSummary
    | SalesforceValidationSummaryWire
    | null;
  /** Field list for validation summary labels (api name → label) when validationSummary is provided */
  crmFieldTargetsForValidation?: (
    | HubSpotFieldForMapping
    | SalesforceFieldForMapping
  )[];
  crm: 'hubspot' | 'salesforce';
  /**
   * When true (Salesforce sync to Account), `salesforceAccountNameMapped`
   * controls whether the “no match” section warns about Account Name for creates.
   */
  salesforceTargetIsAccount?: boolean;
  /** True when Account Name (`Name`) is mapped. If false for Account sync, show create guidance. */
  salesforceAccountNameMapped?: boolean;
  /**
   * Salesforce Account: `not_found` keys whose Account Name spreadsheet cell is blank (`Name` is mapped).
   * Shown under the Account Name warning for creates.
   */
  salesforceNotFoundKeysBlankMappedAccountName?: string[];
  /** Salesforce preview: ambiguous lookup resolutions (optional). */
  lookupSuggestions?: UpsertMappedPreviewResponse['lookup_suggestions'];
  lookupSelections?: UserConfirmedLookups;
  onLookupSelectionChange?: (
    fieldApiName: string,
    sourceValue: string,
    salesforceId: string | null
  ) => void;
  /** Salesforce only: spreadsheet rows that share one primary-key value — user picks which row wins. */
  inputDuplicateGroups?: SalesforceInputDuplicateGroup[] | null;
  inputDuplicateSelections?: Record<string, number>;
  onInputDuplicateSelectionChange?: (
    normalizedPrimaryKey: string,
    spreadsheetRowIndex: number
  ) => void;
  /** Sync batch rows aligned with duplicate `row_indices` (typically pending Salesforce spreadsheet rows). */
  inputDuplicatePreviewRows?: Record<string, unknown>[] | null;
  /**
   * Non–search-field mapped spreadsheet columns used to differentiate duplicate rows (up to
   * three non-empty cells shown per radio option).
   */
  inputDuplicateHintFields?: DuplicateRowHintField[];
}

export default function SyncConflictModal({
  open,
  searchResults,
  userSelections,
  onSelectionChange,
  onConfirm,
  onCancel,
  keyDisplayMap,
  confirmLabel = 'Sync with choices',
  disabled = false,
  validationSummary = null,
  crmFieldTargetsForValidation = [],
  crm,
  salesforceTargetIsAccount = false,
  salesforceAccountNameMapped = true,
  salesforceNotFoundKeysBlankMappedAccountName = [],
  lookupSuggestions = null,
  lookupSelections = {},
  onLookupSelectionChange,
  inputDuplicateGroups = null,
  inputDuplicateSelections = {},
  onInputDuplicateSelectionChange,
  inputDuplicatePreviewRows = null,
  inputDuplicateHintFields = []
}: SyncConflictModalProps) {
  const theme = useTheme();
  const primary =
    theme.customColors?.themePrimary || theme.palette.primary.main;
  const warning =
    theme.customColors?.themeWarningMain ?? theme.palette.warning.main;
  const neutralPrimary =
    theme.customColors?.themeNeutralPrimaryColor ?? theme.palette.text.primary;
  const neutralSecondary =
    theme.customColors?.themeNeutralSecondaryColor ??
    theme.palette.text.secondary;
  const success =
    theme.customColors?.themeSuccess ?? theme.palette.success.main;
  const crmLabel = crm === 'salesforce' ? 'Salesforce' : 'HubSpot';

  const conflictEntries = useMemo(
    () => Object.entries(searchResults?.results || {}),
    [searchResults]
  );
  const conflictEntryCount = conflictEntries.length;
  const conflictTotalPages = Math.max(
    1,
    Math.ceil(conflictEntryCount / CONFLICT_LIST_PAGE_SIZE)
  );
  const [conflictListPage, setConflictListPage] = useState(0);

  useEffect(() => {
    if (!open) return;
    setConflictListPage(0);
  }, [open, searchResults]);

  useEffect(() => {
    setConflictListPage((p) => Math.min(p, conflictTotalPages - 1));
  }, [conflictTotalPages]);

  const conflictPageSafe = Math.min(conflictListPage, conflictTotalPages - 1);
  const conflictPageStart = conflictPageSafe * CONFLICT_LIST_PAGE_SIZE;
  const conflictPageEntries = conflictEntries.slice(
    conflictPageStart,
    conflictPageStart + CONFLICT_LIST_PAGE_SIZE
  );

  if (!searchResults) return null;

  const hasResults = Object.keys(searchResults.results || {}).length > 0;
  const hasNotFound = (searchResults.not_found || []).length > 0;
  const salesforceAccountNameCreateWarning =
    crm === 'salesforce' &&
    salesforceTargetIsAccount &&
    hasNotFound &&
    !salesforceAccountNameMapped;
  const sfBlankMappedNameNotFoundKeys =
    crm === 'salesforce' && salesforceTargetIsAccount
      ? salesforceNotFoundKeysBlankMappedAccountName
      : [];
  const salesforceAccountNameCellBlankBlocksCreates =
    salesforceAccountNameMapped &&
    sfBlankMappedNameNotFoundKeys.length > 0 &&
    hasNotFound;
  const salesforceAccountNameCreateBlocked =
    salesforceAccountNameCreateWarning ||
    salesforceAccountNameCellBlankBlocksCreates;
  const sfAccountNameIssueKeys = salesforceAccountNameCreateWarning
    ? searchResults.not_found
    : sfBlankMappedNameNotFoundKeys;
  const sfAccountNameIssueKeySet = new Set(sfAccountNameIssueKeys);
  const sfNotFoundKeysOkForCreate =
    searchResults.not_found?.filter((k) => !sfAccountNameIssueKeySet.has(k)) ??
    [];

  const hasLookupSuggestions =
    crm === 'salesforce' && hasLookupSuggestionChoices(lookupSuggestions);
  const hasInputDuplicateChoices =
    crm === 'salesforce' &&
    !!inputDuplicateGroups?.some((g) => g.row_indices.length > 1);
  const showResolveTitle =
    hasResults || hasLookupSuggestions || hasInputDuplicateChoices;
  const displayKey = (key: string) =>
    keyDisplayMap?.get(key) ?? formatCanonicalCompositeKeyForDisplay(key);
  const getFieldLabelForLookup = (apiName: string) => {
    const f = crmFieldTargetsForValidation.find((x) => x.apiName === apiName);
    return f?.label ?? apiName;
  };
  const cardBorder = alpha(neutralSecondary, 0.3);
  const cardBg = alpha(primary, 0.02);
  const optionBorderSelected = primary;
  const optionBorderDefault = alpha(neutralSecondary, 0.3);

  const matchKeyChipRow = (keys: string[], tone: 'warning' | 'primary') => {
    const isW = tone === 'warning';
    const bg = isW ? alpha(warning, 0.1) : alpha(primary, 0.1);
    const fg = isW ? alpha(warning, 0.85) : primary;
    const bd = isW ? alpha(warning, 0.35) : alpha(primary, 0.25);
    return (
      <Box
        display="flex"
        flexWrap="wrap"
        gap={1}
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        {keys.slice(0, 10).map((key) => (
          <Chip
            key={key}
            label={displayKey(key)}
            size="small"
            sx={{
              maxWidth: '100%',
              backgroundColor: bg,
              color: fg,
              border: '1px solid',
              borderColor: bd,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}
          />
        ))}
        {keys.length > 10 && (
          <Chip
            label={`+ ${keys.length - 10} more`}
            size="small"
            sx={{
              backgroundColor: bg,
              color: fg,
              fontStyle: 'italic',
              border: '1px solid',
              borderColor: bd
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: '1px solid',
          borderColor: cardBorder,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          color: neutralPrimary,
          borderBottom: '1px solid',
          borderColor: cardBorder,
          pb: 2
        }}
      >
        {showResolveTitle
          ? hasResults
            ? 'Resolve duplicate records'
            : hasInputDuplicateChoices
              ? 'Resolve spreadsheet duplicates'
              : 'Resolve sync choices'
          : `Confirm sync to ${crmLabel}`}
      </DialogTitle>
      <DialogContent sx={{ pt: 2, overflowX: 'hidden' }}>
        <Typography variant="body2" sx={{ mb: 2, color: neutralSecondary }}>
          {hasResults ? (
            <>
              Some rows match existing {crmLabel} records. Choose to update the
              existing record with your spreadsheet data, or ignore the row and
              keep the current CRM record as-is.
              {hasInputDuplicateChoices && (
                <>
                  {' '}
                  Multiple spreadsheet rows share the same primary-key value
                  below — pick one row per match key.
                </>
              )}
            </>
          ) : hasInputDuplicateChoices ? (
            `Multiple spreadsheet rows share the same primary key. For each group, choose the row whose data should be synced; other duplicates in your selection will not be sent.`
          ) : hasLookupSuggestions ? (
            `Review validations and resolve any lookup or primary-key choices below, then confirm to sync your data to ${crmLabel}.`
          ) : (
            `Review the sync summary and validations below, then confirm to sync your data to ${crmLabel}.`
          )}
        </Typography>

        {validationSummary && crmFieldTargetsForValidation.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <CrmValidationSummarySection
              title={
                crm === 'hubspot'
                  ? HUBSPOT_VALIDATION_SUMMARY_TITLE
                  : SALESFORCE_VALIDATION_SUMMARY_TITLE
              }
              description={
                crm === 'hubspot'
                  ? HUBSPOT_VALIDATION_SUMMARY_DESCRIPTION
                  : SALESFORCE_VALIDATION_SUMMARY_DESCRIPTION
              }
              validationSummary={validationSummary}
              fieldTargets={crmFieldTargetsForValidation}
            />
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {hasInputDuplicateChoices &&
          inputDuplicateGroups &&
          onInputDuplicateSelectionChange && (
            <>
              {!(
                validationSummary && crmFieldTargetsForValidation.length > 0
              ) && <Divider sx={{ my: 2 }} />}
              <Box sx={{ mb: 2, minWidth: 0, maxWidth: '100%' }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ color: neutralPrimary }}
                >
                  Duplicate rows in spreadsheet
                </Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  sx={{ minWidth: 0, maxWidth: '100%' }}
                >
                  {inputDuplicateGroups
                    .filter((g) => g.row_indices.length > 1)
                    .map((g) => {
                      const sortedIndices = [...g.row_indices].sort(
                        (a, b) => a - b
                      );
                      const fallback = sortedIndices[0];
                      const picked =
                        inputDuplicateSelections[g.normalized_primary_key] ??
                        fallback;
                      const selectedValue = sortedIndices.includes(picked)
                        ? picked
                        : fallback;
                      const keyLabel =
                        displayKey(g.normalized_primary_key) ||
                        g.display_label ||
                        '—';

                      return (
                        <Box
                          key={g.normalized_primary_key}
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: cardBorder,
                            backgroundColor: cardBg,
                            minWidth: 0,
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            gutterBottom
                            sx={{ color: neutralPrimary }}
                          >
                            Same primary key:&nbsp;&quot;{keyLabel}&quot;
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mb: 1.5, color: neutralSecondary }}
                          >
                            {sortedIndices.length} rows in your selection —
                            choose one
                          </Typography>
                          <FormControl
                            component="fieldset"
                            fullWidth
                            disabled={disabled}
                          >
                            <RadioGroup
                              value={String(selectedValue)}
                              onChange={(e) => {
                                const next = Number(e.target.value);
                                if (
                                  Number.isFinite(next) &&
                                  sortedIndices.includes(next)
                                ) {
                                  onInputDuplicateSelectionChange(
                                    g.normalized_primary_key,
                                    next
                                  );
                                }
                              }}
                            >
                              {sortedIndices.map((rowIdx, ord) => {
                                const row = inputDuplicatePreviewRows?.[
                                  rowIdx
                                ] as Record<string, unknown> | undefined;
                                const hints = duplicateRowHintsForDisplay(
                                  row,
                                  inputDuplicateHintFields,
                                  DUPLICATE_ROW_HINT_MAX,
                                  DUPLICATE_CELL_DISPLAY_MAXLEN
                                );
                                return (
                                  <FormControlLabel
                                    key={rowIdx}
                                    value={String(rowIdx)}
                                    control={<Radio size="small" />}
                                    sx={{
                                      alignItems: 'flex-start',
                                      ml: 0,
                                      mb: 0.5,
                                      mr: 0
                                    }}
                                    label={
                                      <Box sx={{ pt: 0.25, minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: neutralPrimary,
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere'
                                          }}
                                        >
                                          Row {rowIdx + 1} ({ord + 1} of{' '}
                                          {sortedIndices.length})
                                        </Typography>
                                        {hints.length > 0 && (
                                          <Box
                                            component="ul"
                                            sx={{
                                              m: 0,
                                              mt: 0.5,
                                              pl: 2,
                                              listStyle: 'disc'
                                            }}
                                          >
                                            {hints.map((h, hi) => (
                                              <Typography
                                                key={`${rowIdx}-${hi}-${h.label}`}
                                                component="li"
                                                variant="caption"
                                                sx={{
                                                  color: neutralSecondary,
                                                  wordBreak: 'break-word',
                                                  overflowWrap: 'anywhere',
                                                  pb: 0.25,
                                                  '& strong': {
                                                    color: neutralPrimary,
                                                    fontWeight: 600
                                                  }
                                                }}
                                              >
                                                <strong>{h.label}:</strong>{' '}
                                                {h.value}
                                              </Typography>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    }
                                  />
                                );
                              })}
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      );
                    })}
                </Box>
              </Box>
              {hasLookupSuggestions &&
                lookupSuggestions &&
                onLookupSelectionChange && <Divider sx={{ my: 2 }} />}
            </>
          )}

        {hasLookupSuggestions &&
          lookupSuggestions &&
          onLookupSelectionChange && (
            <>
              {!(
                validationSummary && crmFieldTargetsForValidation.length > 0
              ) &&
                !(
                  hasInputDuplicateChoices &&
                  inputDuplicateGroups &&
                  onInputDuplicateSelectionChange
                ) && <Divider sx={{ my: 2 }} />}
              <SalesforceLookupSuggestionsSection
                lookupSuggestions={lookupSuggestions}
                lookupSelections={lookupSelections}
                onLookupSelectionChange={onLookupSelectionChange}
                getFieldLabel={getFieldLabelForLookup}
                disabled={disabled}
              />
            </>
          )}

        {hasLookupSuggestions && hasResults && <Divider sx={{ my: 2 }} />}

        {hasResults && (
          <Box sx={{ mb: 2, minWidth: 0, maxWidth: '100%' }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              gutterBottom
              sx={{ color: neutralPrimary }}
            >
              Existing records found
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ minWidth: 0, maxWidth: '100%' }}
            >
              {conflictPageEntries.map(([normalizedKey, results]) => {
                const currentSelection = userSelections[normalizedKey] ?? null;
                const exactMatch = results.find((r) => r.exact_match);
                const defaultValue =
                  exactMatch?.id ?? (results.length > 0 ? results[0].id : null);
                const selectedValue =
                  currentSelection === null || currentSelection === IGNORE_VALUE
                    ? IGNORE_VALUE
                    : currentSelection || defaultValue || '';

                return (
                  <Box
                    key={normalizedKey}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: cardBorder,
                      backgroundColor: cardBg,
                      minWidth: 0,
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      overflow: 'hidden'
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                      sx={{ color: neutralPrimary }}
                    >
                      Match key: &quot;{displayKey(normalizedKey)}&quot;
                    </Typography>
                    <FormControl
                      component="fieldset"
                      fullWidth
                      disabled={disabled}
                      sx={{ minWidth: 0, maxWidth: '100%' }}
                    >
                      <RadioGroup
                        value={selectedValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          onSelectionChange(
                            normalizedKey,
                            v === IGNORE_VALUE ? null : v
                          );
                        }}
                      >
                        {results.map((result: SearchResult) => {
                          const isSelected = selectedValue === result.id;
                          const recordName =
                            result.name ||
                            result.email ||
                            result.domain ||
                            'N/A';
                          const secondaryText =
                            result.email || result.domain
                              ? [result.email, result.domain]
                                  .filter(Boolean)
                                  .join(' · ')
                              : '';

                          return (
                            <FormControlLabel
                              key={result.id}
                              value={result.id}
                              control={<Radio sx={{ display: 'none' }} />}
                              label={
                                <Box
                                  display="flex"
                                  flexWrap="wrap"
                                  alignItems="center"
                                  gap={1}
                                  sx={{
                                    width: '100%',
                                    maxWidth: '100%',
                                    minWidth: 0,
                                    boxSizing: 'border-box',
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '2px solid',
                                    borderColor: isSelected
                                      ? optionBorderSelected
                                      : optionBorderDefault,
                                    cursor: disabled ? 'default' : 'pointer',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      borderColor: disabled
                                        ? undefined
                                        : optionBorderSelected
                                    }
                                  }}
                                  onClick={() =>
                                    !disabled &&
                                    onSelectionChange(normalizedKey, result.id)
                                  }
                                >
                                  <Radio
                                    checked={isSelected}
                                    disabled={disabled}
                                    size="small"
                                    sx={{
                                      p: 0,
                                      flexShrink: 0,
                                      color: neutralSecondary,
                                      '&.Mui-checked': { color: primary }
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                      sx={{
                                        color: neutralPrimary,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere'
                                      }}
                                    >
                                      {recordName}
                                    </Typography>
                                    {secondaryText && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: neutralSecondary,
                                          wordBreak: 'break-word',
                                          overflowWrap: 'anywhere'
                                        }}
                                      >
                                        {secondaryText}
                                      </Typography>
                                    )}
                                  </Box>
                                  {result.exact_match && (
                                    <Chip
                                      icon={<CheckCircleIcon />}
                                      label="Exact match"
                                      size="small"
                                      sx={{
                                        height: 20,
                                        flexShrink: 0,
                                        maxWidth: 'calc(100% - 8px)',
                                        backgroundColor: alpha(success, 0.1),
                                        color: success,
                                        border: `1px solid ${alpha(success, 0.3)}`,
                                        '& .MuiChip-icon': { color: success }
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              sx={{
                                m: 0,
                                mb: 1,
                                width: '100%',
                                maxWidth: '100%',
                                alignItems: 'flex-start',
                                '& .MuiFormControlLabel-label': {
                                  maxWidth: '100%',
                                  minWidth: 0
                                }
                              }}
                            />
                          );
                        })}
                        <FormControlLabel
                          value={IGNORE_VALUE}
                          control={<Radio sx={{ display: 'none' }} />}
                          label={
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box',
                                p: 1.5,
                                borderRadius: 1,
                                border: '2px solid',
                                borderColor:
                                  selectedValue === IGNORE_VALUE
                                    ? optionBorderSelected
                                    : optionBorderDefault,
                                cursor: disabled ? 'default' : 'pointer',
                                overflow: 'hidden',
                                '&:hover': {
                                  borderColor: disabled
                                    ? undefined
                                    : optionBorderSelected
                                }
                              }}
                              onClick={() =>
                                !disabled &&
                                onSelectionChange(normalizedKey, null)
                              }
                            >
                              <Radio
                                checked={selectedValue === IGNORE_VALUE}
                                disabled={disabled}
                                size="small"
                                sx={{
                                  p: 0,
                                  mr: 1,
                                  color: neutralSecondary,
                                  '&.Mui-checked': { color: primary }
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: neutralPrimary,
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                  minWidth: 0
                                }}
                              >
                                Ignore – keep existing record
                              </Typography>
                            </Box>
                          }
                          sx={{
                            m: 0,
                            width: '100%',
                            maxWidth: '100%',
                            alignItems: 'flex-start',
                            '& .MuiFormControlLabel-label': {
                              maxWidth: '100%',
                              minWidth: 0
                            }
                          }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                );
              })}
              {conflictEntryCount > CONFLICT_LIST_PAGE_SIZE && (
                <Stack
                  spacing={1}
                  alignItems="center"
                  sx={{
                    mt: 1,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: cardBorder
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: neutralSecondary }}
                  >
                    Showing {conflictPageStart + 1}–
                    {conflictPageStart + conflictPageEntries.length} of{' '}
                    {conflictEntryCount} duplicate keys
                  </Typography>
                  <Pagination
                    count={conflictTotalPages}
                    page={conflictPageSafe + 1}
                    onChange={(_, next) =>
                      setConflictListPage(Math.max(0, next - 1))
                    }
                    color="primary"
                    size="small"
                    disabled={disabled}
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Stack>
              )}
            </Box>
          </Box>
        )}

        {hasNotFound && (
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            {salesforceAccountNameCreateBlocked && (
              <>
                {salesforceAccountNameCreateWarning && (
                  <Alert
                    severity="warning"
                    sx={{
                      mb: 1,
                      '& .MuiAlert-message': { width: '100%' }
                    }}
                  >
                    To create new accounts in Salesforce, specify an Account
                    Name by mapping the <strong>Account Name</strong> field to a
                    spreadsheet column in CRM mapping. Rows that matched an
                    existing record can still be updated.
                  </Alert>
                )}
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{
                    ...(salesforceAccountNameCreateWarning ? { mt: 1.5 } : {}),
                    color: neutralPrimary
                  }}
                >
                  {salesforceAccountNameCreateWarning
                    ? `Records with no Salesforce match for their key (${sfAccountNameIssueKeys.length})`
                    : `Records with no Salesforce match and no Account Name value (${sfAccountNameIssueKeys.length})`}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    mb: 1,
                    color: neutralSecondary,
                    lineHeight: 1.4
                  }}
                >
                  {salesforceAccountNameCreateWarning
                    ? 'Without Account Name mapped, sync cannot create accounts for these rows.'
                    : 'Sync cannot create accounts until these rows have a value in the Account Name column.'}
                </Typography>
                {matchKeyChipRow(sfAccountNameIssueKeys, 'warning')}
              </>
            )}
            {sfNotFoundKeysOkForCreate.length > 0 && (
              <>
                {salesforceAccountNameCreateBlocked && (
                  <Divider sx={{ my: 3 }} />
                )}
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ color: neutralPrimary }}
                >
                  No match in {crmLabel} (will create)
                </Typography>
                <Alert
                  severity="info"
                  sx={{
                    mb: 1
                  }}
                >
                  {sfNotFoundKeysOkForCreate.length} match key
                  {sfNotFoundKeysOkForCreate.length !== 1 ? 's' : ''} not found.
                  New records will be created in {crmLabel}.
                </Alert>
                {matchKeyChipRow(sfNotFoundKeysOkForCreate, 'primary')}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: cardBorder,
          gap: 1
        }}
      >
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={(t) => ({
            border: `1px solid ${t.customColors?.themePrimary || t.palette.primary.main}`,
            backgroundColor: t.customColors?.themeSecondary,
            color: t.customColors?.themePrimary || t.palette.primary.main,
            borderRadius: '50px',
            textTransform: 'capitalize'
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={disabled}
          sx={(t) => ({
            border: `1px solid ${t.customColors?.themePrimary || t.palette.primary.main}`,
            backgroundColor:
              t.customColors?.themePrimary || t.palette.primary.main,
            color: t.customColors?.themeSecondary || 'white',
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:disabled': { borderColor: 'inherit', opacity: 0.6 }
          })}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
